import React from "react";
import type { ApiDocumentation } from "../types";
import MobileListWrapper from "@/components/mobile-list-wrapper";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { FileText, Link as LinkIcon } from "lucide-react";

interface ListContentProps {
  data: ApiDocumentation[];
  isLoading: boolean;
  className?: string;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  emptyListComponent: React.ReactElement;
}

function getFileName(url: string): string {
  try {
    return new URL(url).pathname.split("/").filter(Boolean).at(-1) ?? url;
  } catch {
    return url;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }
  return `${Math.round(bytes / 1024)} Ko`;
}

const ListContent = ({
  data,
  isLoading,
  className,
  page,
  pageCount,
  onPageChange,
  emptyListComponent,
}: ListContentProps) => {
  const { t } = useTranslation();
  return (
    <MobileListWrapper
      data={data}
      isLoading={isLoading}
      className={className}
      page={page}
      pageCount={pageCount}
      onPageChange={onPageChange}
      emptyListComponent={emptyListComponent}
      onItemClick={(item) => {
        const url =
          item.kind === "uploaded_file" ? item.uploaded_file : item.link;
        window.open(url, "_blank", "noopener,noreferrer");
      }}
      renderItem={(item) => {
        const first = item.dilemmas[0];
        const extra = item.dilemmas.length - 1;
        return (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-sm">
                  {first?.reference ?? "—"}
                </span>
                {extra > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                    +{extra}
                  </Badge>
                )}
              </div>
              <span className="text-xs text-gray-500 shrink-0">
                {item.kind === "uploaded_file"
                  ? formatFileSize(item.file_size)
                  : "—"}
              </span>
            </div>
            <div className="mt-1">
              <p className="text-sm text-gray-700 truncate">
                {first?.name ?? "—"}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {item.kind === "uploaded_file" ? (
                  <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                ) : (
                  <LinkIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                )}
                <p
                  className={`text-xs text-gray-400 truncate ${
                    item.kind === "uploaded_file" ? "font-mono" : ""
                  }`}
                >
                  {item.kind === "uploaded_file"
                    ? getFileName(item.uploaded_file)
                    : item.link}
                </p>
              </div>
            </div>
            <p className="text-xs text-primary mt-1">
              {t("modules.docs.actions.tap_to_open")}
            </p>
          </>
        );
      }}
    />
  );
};

export default ListContent;
